import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, RefreshCcw, ShieldOff, Smartphone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

export type HandheldDeviceStatus = 'active' | 'pending' | 'revoked';

export interface HandheldDevice {
  id: string;
  label: string;
  locationName?: string | null;
  status: HandheldDeviceStatus;
  heartbeatAt?: Date | null;
  tokenRotatedAt?: Date | null;
  tokenLastFour?: string | null;
}

export interface HandheldDeviceManagerProps {
  devices: HandheldDevice[];
  onProvision?: () => void;
  onRotate?: (deviceId: string) => void;
  onRevoke?: (deviceId: string) => void;
}

const statusVariant: Record<HandheldDeviceStatus, 'default' | 'secondary' | 'destructive'> = {
  active: 'default',
  pending: 'secondary',
  revoked: 'destructive',
};

const statusLabelKey: Record<HandheldDeviceStatus, string> = {
  active: 'settings:handheld.deviceManager.status.active',
  pending: 'settings:handheld.deviceManager.status.pending',
  revoked: 'settings:handheld.deviceManager.status.revoked',
};

const summarize = (devices: HandheldDevice[]) => {
  const byStatus = devices.reduce<Record<HandheldDeviceStatus, number>>(
    (acc, device) => {
      acc[device.status] += 1;
      return acc;
    },
    { active: 0, pending: 0, revoked: 0 },
  );
  return byStatus;
};

const formatRelative = (value?: Date | null, fallback?: string) => {
  if (!value) {
    return fallback ?? '-';
  }
  try {
    return formatDistanceToNow(value, { addSuffix: true });
  } catch {
    return fallback ?? '-';
  }
};

export function HandheldDeviceManager({
  devices,
  onProvision,
  onRotate,
  onRevoke,
}: HandheldDeviceManagerProps) {
  const { t } = useTranslation(['common', 'settings']);
  const summary = summarize(devices);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Smartphone className="h-4 w-4 text-emerald-500" aria-hidden />
            {t('settings:handheld.deviceManager.title', { defaultValue: 'Handheld devices' })}
          </CardTitle>
          <CardDescription>
            {t('settings:handheld.deviceManager.subtitle', {
              defaultValue: 'Provision, rotate, and monitor handheld tokens for each location.',
            })}
          </CardDescription>
        </div>
        <Button onClick={onProvision}>
          {t('settings:handheld.deviceManager.actions.provision', { defaultValue: 'Provision device' })}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          {(['active', 'pending', 'revoked'] as HandheldDeviceStatus[]).map((status) => (
            <Badge key={status} variant={statusVariant[status]} className="px-3 py-1 text-xs font-medium">
              {t(statusLabelKey[status])}: {summary[status]}
            </Badge>
          ))}
        </div>

        <Separator />

        {devices.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/60 p-6 text-sm text-muted-foreground">
            {t('settings:handheld.deviceManager.empty', {
              defaultValue: 'No handheld devices have been provisioned yet.',
            })}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings:handheld.deviceManager.columns.label', { defaultValue: 'Label' })}</TableHead>
                <TableHead>{t('settings:handheld.deviceManager.columns.location', { defaultValue: 'Location' })}</TableHead>
                <TableHead>{t('settings:handheld.deviceManager.columns.heartbeat', { defaultValue: 'Last heartbeat' })}</TableHead>
                <TableHead>{t('settings:handheld.deviceManager.columns.tokenAge', { defaultValue: 'Token age' })}</TableHead>
                <TableHead className="text-right">
                  {t('settings:handheld.deviceManager.columns.actions', { defaultValue: 'Actions' })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id} data-status={device.status}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{device.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {t(statusLabelKey[device.status])}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{device.locationName ?? t('common:labels.notAssigned', { defaultValue: 'Unassigned' })}</TableCell>
                  <TableCell>
                    {formatRelative(device.heartbeatAt, t('settings:handheld.deviceManager.labels.never', { defaultValue: 'Never' }))}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      {formatRelative(device.tokenRotatedAt, t('settings:handheld.deviceManager.labels.never', {
                        defaultValue: 'Never',
                      }))}
                      {device.tokenLastFour ? (
                        <Badge variant="outline" className="text-[11px]">
                          {t('settings:handheld.deviceManager.labels.token', {
                            defaultValue: 'Ending •{{lastFour}}',
                            lastFour: device.tokenLastFour,
                          })}
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label={t('settings:handheld.deviceManager.actions.more', { defaultValue: 'Open actions menu' })}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{device.label}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className={cn(onRotate ? '' : 'pointer-events-none opacity-50')}
                          onSelect={() => onRotate?.(device.id)}
                        >
                          <RefreshCcw className="mr-2 h-4 w-4" aria-hidden />
                          {t('settings:handheld.deviceManager.actions.rotate', { defaultValue: 'Rotate token' })}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className={cn('text-destructive', onRevoke ? '' : 'pointer-events-none opacity-50')}
                          onSelect={() => onRevoke?.(device.id)}
                        >
                          <ShieldOff className="mr-2 h-4 w-4" aria-hidden />
                          {t('settings:handheld.deviceManager.actions.revoke', { defaultValue: 'Revoke device' })}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default HandheldDeviceManager;
